<template>
    <div class="messages-page-container">
        <div class="messages-header">
            <breadcrumbs />
            <page-title :text="$t('page_title.messages')" />

            <!-- Tabs Navigation - Only shown for TO1 users -->
            <div v-if="isTO1User" class="tabs-container">
                <v-card flat width="600" class="mx-auto">
                    <v-tabs
                        v-model="activeTab"
                        bg-color="white"
                        color="var(--zsh-primary)"
                        align-tabs="center"
                        @update:model-value="onTabChange"
                    >
                        <v-tab value="dashboard" :class="{'active-tab': activeTab === 'dashboard'}">
                            <v-icon start>mdi-view-dashboard</v-icon>
                            {{ $t('messages.dashboard') }}
                        </v-tab>
                        <v-tab value="hotellier" :class="{'active-tab': activeTab === 'hotellier'}">
                            <v-icon start>mdi-domain</v-icon>
                            {{ $t('messages.hotellier_messages') }}
                        </v-tab>
                        <v-tab value="customer" :class="{'active-tab': activeTab === 'customer'}">
                            <v-icon start>mdi-account-group</v-icon>
                            {{ $t('messages.customer_messages') }}
                        </v-tab>
                    </v-tabs>
                </v-card>
            </div>
        </div>

        <!-- Tab Content -->
        <div class="messages-content">
            <!-- Dashboard Tab -->
            <div v-if="activeTab === 'dashboard'" class="h-100 messaging-tab-content">
                <dashboard-tab />
            </div>

            <!-- Hotellier Messages Tab -->
            <div v-else-if="activeTab === 'hotellier'" class="h-100 messaging-tab-content">
                <!-- Only render when ticket types are loaded -->
                <div v-if="!isLoadingTicketTypes && _hotelierTicketTypeIds.length > 0" class="h-100">
                    <hotellier-messaging :hotellier-ticket-type-ids="_hotelierTicketTypeIds" />
                </div>
                <div v-else-if="isLoadingTicketTypes" class="d-flex justify-center align-center h-100">
                    <v-progress-circular indeterminate color="var(--zsh-primary)" class="mr-3" />
                    <span>{{ $t('dashboard.loading_ticket_types') }}</span>
                </div>
                <div v-else class="d-flex justify-center align-center h-100">
                    <v-alert type="info" density="compact">{{ $t('dashboard.no_applicable_ticket_types') }}</v-alert>
                </div>
            </div>

            <!-- Customer Messages Tab -->
            <div v-else-if="activeTab === 'customer'" class="h-100 messaging-tab-content">
                <div v-if="isTO1User" class="h-100">
                    <!-- Only render when ticket types are loaded -->
                    <div v-if="!isLoadingTicketTypes && _customerTicketTypeIds.length > 0" class="h-100">
                        <customer-messaging :customer-ticket-type-ids="_customerTicketTypeIds" />
                    </div>
                    <div v-else-if="isLoadingTicketTypes" class="d-flex justify-center align-center h-100">
                        <v-progress-circular indeterminate color="var(--zsh-primary)" class="mr-3" />
                        <span>Loading ticket types...</span>
                    </div>
                    <div v-else class="d-flex justify-center align-center h-100">
                        <v-alert type="info" density="compact"
                            >No applicable ticket types found for Customer messages.</v-alert
                        >
                    </div>
                </div>
                <div v-else class="d-flex justify-center align-center h-100">
                    <v-alert type="warning" class="mx-auto">
                        Access to Customer Messages is restricted to TO1 role users only.
                    </v-alert>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    // console.log('[DEBUG] MessagesPage: SCRIPT EXECUTION STARTED'); // Removed temp log
    import {Component, Vue, Watch} from 'vue-facing-decorator' // Removed Setup
    import {authStore} from '@/composables/store-accessor'
    import HotellierMessaging from '../components/messages/HotellierMessaging.vue'
    import CustomerMessaging from '../components/messages/CustomerMessaging.vue'
    import DashboardTab from '../components/messages/DashboardTab.vue'
    import Breadcrumbs from '~src/components/Breadcrumbs.vue'
    import PageTitle from '@/components/parts/PageTitle.vue'
    import {useTicketTypes} from '../composables/useTicketTypes'
    // import type { Ref } from 'vue' // No longer needed here for class props

    // Initialize composable at module scope
    const ticketManager = useTicketTypes()

    @Component({
        components: {
            HotellierMessaging,
            CustomerMessaging,
            DashboardTab,
            Breadcrumbs,
            PageTitle,
        },
    })
    export class MessagesPage extends Vue {
        activeTab: string = 'hotellier' // Will be updated in mounted() based on user role

        // @Setup method and class property declarations for composable values are removed.

        get isTO1User(): boolean {
            return authStore.isTO1
        }

        get _hotelierTicketTypeIds(): string[] {
            // Access directly from module-scoped ticketManager
            return ticketManager.hotelierTicketTypeIds.value
        }

        get _customerTicketTypeIds(): string[] {
            return ticketManager.customerTicketTypeIds.value
        }

        get isLoadingTicketTypes(): boolean {
            return ticketManager.isLoadingTicketTypes.value
        }

        async mounted() {
            // console.log('[DEBUG] MessagesPage: mounted hook executing...');
            // console.log('[DEBUG] MessagesPage: mounted - typeof ticketManager.loadTicketTypes at start of mounted:', typeof ticketManager.loadTicketTypes);

            // Set default tab based on user role
            if (this.isTO1User) {
                this.activeTab = 'dashboard'
                console.log('MessagesPage: TO1 user detected, setting default tab to dashboard')
            } else {
                this.activeTab = 'hotellier'
                console.log('MessagesPage: Non-TO1 user detected, setting default tab to hotellier')
            }

            console.log('MessagesPage: Initializing ticket types via module-scoped composable...')
            await this.callLoadTicketTypes()
        }

        // Method to handle tab changes
        async onTabChange(value: unknown) {
            // Cast the unknown value to string
            const newTab = value as string
            console.log(`Tab changed to ${newTab}`)
            this.activeTab = newTab // Update activeTab first

            // Prevent non-TO1 users from accessing tabs other than 'hotellier'
            if (!this.isTO1User && newTab !== 'hotellier') {
                console.log('Non-TO1 user attempting to access restricted tab, redirecting to hotellier tab')
                this.activeTab = 'hotellier' // Force back to hotellier
                // Ensure hotellier ticket types are loaded if switching back and they are empty and not currently loading
                if (this._hotelierTicketTypeIds.length === 0 && !this.isLoadingTicketTypes) {
                    console.log('Redirected to Hotellier tab, attempting to load its ticket types.')
                    await this.callLoadTicketTypes()
                }
                return // Return after handling redirection
            }
            // No 'else' needed here, activeTab is already set.

            // If switching to hotellier or customer tab, and their specific ticket types are not yet loaded (empty array),
            // and not currently loading, then trigger a load.
            if (newTab === 'hotellier' && this._hotelierTicketTypeIds.length === 0 && !this.isLoadingTicketTypes) {
                console.log('Hotellier tab selected, attempting to load ticket types.')
                await this.callLoadTicketTypes()
            } else if (
                newTab === 'customer' &&
                this._customerTicketTypeIds.length === 0 &&
                this.isTO1User &&
                !this.isLoadingTicketTypes
            ) {
                console.log('Customer tab selected, attempting to load ticket types.')
                await this.callLoadTicketTypes()
            }

            console.log(
                'Current hotelierTicketTypeIds (from composable via class member):',
                JSON.stringify(this._hotelierTicketTypeIds)
            )
            console.log(
                'Current customerTicketTypeIds (from composable via class member):',
                JSON.stringify(this._customerTicketTypeIds)
            )
        }

        // Renamed to avoid potential conflict with a 'loadTicketTypes' property if returned from setup directly
        async callLoadTicketTypes() {
            // Call directly from module-scoped ticketManager
            if (typeof ticketManager.loadTicketTypes === 'function') {
                await ticketManager.loadTicketTypes()
            } else {
                // This should ideally not happen if useTicketTypes is structured correctly
                console.error(
                    'MessagesPage: ticketManager.loadTicketTypes is not a function! Cannot load ticket types. Type is:',
                    typeof ticketManager.loadTicketTypes
                )
            }
        }
    }

    export default MessagesPage
</script>

<style scoped>
    @import '../assets/styles/messages-page.css';
</style>
